import type React from 'react'
import { detectHomoglyph } from '../utils/homoglyph'
import { isCustomsAnswerStale } from '@crm/shared'
import { STALE_NOTE, formatCustomsDate } from './itemColDefs.customs'
import type { ItemRow } from './itemsTable.types'

/**
 * Подсказка нетарифки под артикулом (Л3): «ТН ВЭД 8536690000 · Честный знак нужен
 * — из архива от 12.03.2026». Показывается, только пока артикул тот же, для
 * которого парсер нашёл прошлую проверку. Нечего сказать — null.
 */
export function customsHintText(row: Pick<ItemRow, 'partNumber' | 'customsHint'>): string | null {
  const hint = row.customsHint
  if (!hint) return null
  if (hint.forPartNumber.trim().toUpperCase() !== row.partNumber.trim().toUpperCase()) return null
  const parts: string[] = []
  if (hint.tnvedCode) parts.push(`ТН ВЭД ${hint.tnvedCode}`)
  if (hint.chestnyZnak === 'YES') {
    const from = formatCustomsDate(hint.chestnyZnakFrom)
    parts.push(from ? `Честный знак нужен с ${from}` : 'Честный знак нужен')
  } else if (hint.chestnyZnak === 'NO') parts.push('Честный знак не нужен')
  if (parts.length === 0) return null
  const date = formatCustomsDate(hint.checkedAt)
  const stale = isCustomsAnswerStale(hint.checkedAt) ? ` · ${STALE_NOTE}` : ''
  return `${parts.join(' · ')} — из архива от ${date || '—'}${stale}`
}

interface PartNumberCellProps {
  row: ItemRow
  /** Подсказки каталога партномеров (общий datalist таблицы). */
  suggestions: Array<{ partNumber: string; brand: string }>
  /** Артикул+бренд встречается в заявке больше одного раза. */
  isDuplicate: boolean
  cellClass: string
  onChange: (field: 'partNumber' | 'brand', value: string) => void
  /** Строка поиска для подсказок каталога. */
  onQuery: (q: string) => void
  onRegister: (el: HTMLInputElement | null) => void
  onKeyDown: (e: React.KeyboardEvent) => void
}

// Ячейка «Part Number»: ввод с подсказками каталога, автоподстановка бренда,
// предупреждения о кириллице-двойниках и дубле артикула.
export function PartNumberCell({
  row,
  suggestions,
  isDuplicate,
  cellClass,
  onChange,
  onQuery,
  onRegister,
  onKeyDown,
}: PartNumberCellProps) {
  const hg = detectHomoglyph(row.partNumber)
  const customsHint = customsHintText(row)
  return (
    <>
      <input
        ref={onRegister}
        value={row.partNumber}
        onChange={(e) => {
          const v = e.target.value.toUpperCase()
          onChange('partNumber', v)
          onQuery(v)
          const match = suggestions.find((s) => s.partNumber.toUpperCase() === v)
          if (match && !row.brand.trim()) {
            onChange('brand', match.brand)
          }
        }}
        onFocus={() => onQuery(row.partNumber)}
        onBlur={() => {
          const match = suggestions.find(
            (s) => s.partNumber.toUpperCase() === row.partNumber.toUpperCase(),
          )
          if (match && !row.brand.trim()) {
            onChange('brand', match.brand)
          }
        }}
        onKeyDown={onKeyDown}
        placeholder="напр. STM32F103"
        list="partnumber-suggestions"
        className={`${cellClass} font-mono ${hg.has ? 'text-red-600' : ''}`}
      />
      {hg.has && (
        <button
          type="button"
          onClick={() => onChange('partNumber', hg.canonical)}
          title="Подставить латинский (канонический) артикул"
          className="mt-0.5 block w-full px-1 text-left text-[10px] leading-tight text-red-600 hover:underline"
        >
          ⚠ кириллица:{' '}
          <span className="font-mono">
            {hg.segments.map((s, i) => (
              <span key={i} className={s.bad ? 'rounded bg-red-100 font-bold' : ''}>
                {s.text}
              </span>
            ))}
          </span>{' '}
          → латиница «{hg.canonical}»
        </button>
      )}
      {!hg.has && isDuplicate && (
        <div className="mt-0.5 px-1 text-[10px] leading-tight text-amber-600">
          ⚠ дубль артикула в заявке
        </div>
      )}
      {customsHint && (
        <div
          className={`mt-0.5 px-1 text-[10px] leading-tight ${
            row.customsHint?.chestnyZnak === 'YES' ? 'text-amber-700' : 'text-gray-500'
          }`}
          title="Нетарифка из прошлой проверки логиста по этому партномеру: ТН ВЭД и «Честный знак». Пошлину и документы логист перепроверит."
        >
          {customsHint}
        </div>
      )}
    </>
  )
}
